import classNames from 'classnames';
import React, { forwardRef, useRef } from 'react';

import { Input, SearchInput } from '@eika/input';
import { Loader } from '@eika/loader';

import { ComboboxProps, SuggestionWithIdAndValue } from './ComboboxTypes';
import { findInSuggestions } from './hooks/filterInSuggestions';
import useComboboxSelect from './hooks/useComboboxSelect';
import { getAriaText, onInputBlur, onWrapperBlur } from './utils';

import './Combobox.scss';

const Combobox = forwardRef<HTMLInputElement, ComboboxProps>(({ id, ...props }, forwardedRef) => {
    const suggestionListId = `${id}-list`;

    const inputRef = useRef<HTMLInputElement | null>(null);
    const inputWrapperRef = useRef<HTMLDivElement | null>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    const suggestionListRef = useRef<HTMLUListElement | null>(null);
    const {
        suggestions,
        value,
        label,
        sublabel,
        error,
        placeholder,
        openOnFocus = true,
        selectOnBlur = true,
        className,
        suggestionListClassName,
        onSelect,
        onChange,
        onFocus,
        onBlur,
        searchInput = false,
        hideSearchIcon = false,
        resetOnEmptyResult = false,
        translations,
        isLoading = false,
        exactMatchOnChange = true,
        ...rest
    } = props;

    const {
        handleSelect,
        suggestionListOpen,
        setSuggestionListOpen,
        navSuggestion,
        setNavSuggestion,
        currentSuggestions,
        navigationOrInputValue,
        onFocus: comboboxOnFocus,
    } = useComboboxSelect(
        suggestions,
        value,
        onChange,
        inputRef,
        wrapperRef,
        resetOnEmptyResult,
        suggestionListRef.current,
        exactMatchOnChange,
        onSelect,
    );

    const inputProps = {
        id,
        className: 'sgw-combobox-input',
        value: navigationOrInputValue,
        sublabel,
        label,
        error,
        placeholder,
        role: 'combobox',
        'aria-label': props['aria-label'],
        'aria-labelledby': props['aria-labelledby'],
        'aria-expanded': !!navSuggestion?.id,
        'aria-activedescendant': suggestionListOpen ? navSuggestion?.id : undefined,
        'aria-controls': suggestionListId,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value),
        onFocus: () => {
            if (openOnFocus && !suggestionListOpen) {
                setSuggestionListOpen(true);
            }
            comboboxOnFocus();
            onFocus?.();
        },
        onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
            const isClickOutsideContainer = onInputBlur(e, wrapperRef, inputWrapperRef, setSuggestionListOpen);

            if (isClickOutsideContainer) {
                if (navSuggestion || value.length > 0) {
                    const firstElement = findInSuggestions(currentSuggestions, value);
                    const targetValue = navSuggestion?.value ?? firstElement?.value ?? value;
                    if (onSelect && selectOnBlur) {
                        onSelect(targetValue);
                    }

                    onBlur?.(targetValue);
                }
                setNavSuggestion(null);
            }
        },
        ref: (el: HTMLInputElement) => {
            inputRef.current = el;
            if (forwardedRef) {
                if (typeof forwardedRef === 'function') {
                    forwardedRef(el);
                } else {
                    forwardedRef.current = el;
                }
            }
        },
        ...rest,
    };

    const onClickSuggestion = (suggestion: SuggestionWithIdAndValue) => {
        new Promise((resolve) => {
            setNavSuggestion(suggestion);
            setTimeout(() => {
                resolve(0);
            }, 1);
        }).then(() => handleSelect(suggestion));
    };

    return (
        <div
            className={classNames('sgw-combobox', className)}
            onBlur={(e) => onWrapperBlur(e, setSuggestionListOpen)}
            ref={wrapperRef}
        >
            <div
                className={classNames('sgw-combobox-input__wrapper', {
                    'showing-suggestions': suggestionListOpen && currentSuggestions.length > 0,
                })}
                ref={inputWrapperRef}
            >
                {searchInput ? (
                    <SearchInput {...inputProps} hideIcon={hideSearchIcon} aria-autocomplete="list" />
                ) : (
                    <Input {...inputProps} aria-autocomplete="list" />
                )}
            </div>
            <span className="screen-reader-text" role="status">
                {getAriaText(currentSuggestions, value, translations)}
            </span>
            <div
                hidden={!suggestionListOpen || !currentSuggestions.length}
                className={classNames(suggestionListClassName, 'sgw-combobox-suggestion-list__wrapper')}
            >
                <ul
                    id={suggestionListId}
                    role="listbox"
                    className={classNames('sgw-combobox-suggestion-list', 'unstyled', {
                        'showing-suggestions': suggestionListOpen && currentSuggestions.length > 0,
                    })}
                    aria-live="polite"
                    aria-busy={isLoading}
                    ref={suggestionListRef}
                >
                    {isLoading ? (
                        <li className="sgw-combobox-suggestion-list__item sgw-combobox-suggestion-list__loading-wrapper">
                            <Loader mini />
                        </li>
                    ) : (
                        currentSuggestions.map((suggestion) => (
                            <li
                                key={suggestion.id}
                                id={suggestion.id}
                                data-focused={navSuggestion?.id === suggestion.id ? true : undefined}
                                role="option"
                                aria-selected={navSuggestion?.id === suggestion.id}
                                tabIndex={-1}
                                onClick={() => onClickSuggestion(suggestion)}
                                onKeyDown={() => null}
                                className={classNames('sgw-combobox-suggestion-list__item', {
                                    'in-focus': navSuggestion?.id === suggestion.id,
                                })}
                            >
                                {suggestion.customLabel ?? suggestion.label ?? suggestion.value}
                            </li>
                        ))
                    )}
                </ul>
            </div>
        </div>
    );
});

export default Combobox;
